import React from "react";
import { Link } from "react-router-dom";
import PostDetailLockIcon from "./PostDetailLockIcon";
import PostDetailMailIcon from "./PostDetailMailIcon";
import PostDetailCalendarIcon from "./PostDetailCalendarIcon";
import PostDetailMessageCircleIcon from "./PostDetailMessageCircleIcon";
import PostDetailShareMenu from "./PostDetailShareMenu";
import PostDetailTooltip from "./PostDetailTooltip";
import { formatPostDate } from "../Members/memberUtils";
import {
  formatPostDetailDate,
  getPostTypeLabel,
  isFreePublicPost,
  isPremiumLocked,
} from "./postDetailUtils";
import {
  PostDetailActionButton,
  PostDetailActions,
  PostDetailAuthorAvatar,
  PostDetailAuthorBlock,
  PostDetailAuthorName,
  PostDetailAuthorTitle,
  PostDetailAuthorVerified,
  PostDetailCategoryBadge,
  PostDetailContentCol,
  PostDetailCover,
  PostDetailDateBlock,
  PostDetailDateLabel,
  PostDetailDateValue,
  PostDetailEmailFields,
  PostDetailEmailForm,
  PostDetailEmailGateText,
  PostDetailEmailPreview,
  PostDetailExcerpt,
  PostDetailFeaturedRibbon,
  PostDetailGateActions,
  PostDetailGateCard,
  PostDetailGateIcon,
  PostDetailGateText,
  PostDetailGateTitle,
  PostDetailGrid,
  PostDetailHeader,
  PostDetailHeroButton,
  PostDetailHtmlContent,
  PostDetailIconButton,
  PostDetailMetaDivider,
  PostDetailMetaRow,
  PostDetailMetaStats,
  PostDetailOutlineButton,
  PostDetailSidebar,
  PostDetailSidebarCta,
  PostDetailSidebarTitle,
  PostDetailSuggestedAuthor,
  PostDetailSuggestedLink,
  PostDetailSuggestedList,
  PostDetailSuggestedMeta,
  PostDetailSuggestedType,
  PostDetailTag,
  PostDetailTags,
  PostDetailTagsFooter,
  PostDetailTitle,
  PostDetailTitleRow,
  PostDetailGlassCard,
} from "./PostDetail.styles";
import { getPublisherAvatarUrl, stripPostInlineStyles } from "../../utils";
import PostDetailVideoPlayer from "./PostDetailVideoPlayer";

const getCategoryLabel = (post) => {
  if (post?.tags?.[0]?.name) return post.tags[0].name;
  if (post?.educationTopics?.[0]?.name) return post.educationTopics[0].name;
  return getPostTypeLabel(post);
};

const PostDetailContent = ({
  post,
  user,
  publicAccessVerified,
  publicAccessError,
  checkPublicAccessConfirmed,
  subscribeToPublic,
  publicAccessName,
  publicAccessEmail,
  onPublicEmailInput,
  onPublicNameInput,
  suggestedPosts = [],
  commentsCount = 0,
  commentsSlot = null,
  isPreview = false,
}) => {
  if (!post) {
    return null;
  }

  const publisher = post.author || {};
  const content =
    post.content &&
    stripPostInlineStyles(
      post.content.replace(`data-jodit_iframe_wrapper="1"`, "")
    );

  const excerptHtml = stripPostInlineStyles(post.excerpt || "");
  const locked = isPremiumLocked(post, user);
  const freePublic = isFreePublicPost(post);
  /** Legacy: hidden class only for guest free-public Wistia/content before email unlock. */
  const isPublicAccessHidden =
    !user && freePublic && publicAccessVerified !== true ? "hidden" : "";

  const showVideoForMember =
    user && post.embeddedVideo && post.contentTypeId === 2;
  const showVideoForGuest =
    !user &&
    post.embeddedVideo &&
    post.contentTypeId === 2 &&
    freePublic &&
    publicAccessVerified === true;
  const showVideo = showVideoForMember || showVideoForGuest;

  const showMemberContent = user && post.content !== undefined;
  const showGuestFreeContent =
    freePublic &&
    post.content !== undefined &&
    publicAccessVerified === true;
  const showBody = showMemberContent || showGuestFreeContent;

  const hasFullAccess =
    !locked && (Boolean(user) || (freePublic && publicAccessVerified === true));
  const showSidebar = suggestedPosts.length > 0;

  return (
    <>
      <PostDetailHeader>
        {publisher.isFeaturedContributor && (
          <PostDetailFeaturedRibbon>Featured Contributor</PostDetailFeaturedRibbon>
        )}

        <PostDetailCategoryBadge>{getCategoryLabel(post)}</PostDetailCategoryBadge>

        <PostDetailTitleRow>
          {post.image?.original && (
            <PostDetailCover
              src={post.image.original}
              alt={post.title}
              loading="lazy"
            />
          )}
          <PostDetailTitle>{post.title}</PostDetailTitle>
        </PostDetailTitleRow>

        <PostDetailMetaRow>
          <PostDetailAuthorBlock
            to={`/analyst/${publisher.slug || ""}`}
            onClick={isPreview ? (event) => event.preventDefault() : undefined}
            tabIndex={isPreview ? -1 : undefined}
          >
            <PostDetailAuthorAvatar>
              {getPublisherAvatarUrl(publisher) ? (
                <img
                  src={getPublisherAvatarUrl(publisher)}
                  alt={publisher.name || ""}
                />
              ) : (
                <span className="post-detail-author-fallback" aria-hidden="true">
                  {String(publisher.name || "?").slice(0, 1)}
                </span>
              )}
              <PostDetailAuthorVerified aria-hidden="true">
                <span className="material-icons">check</span>
              </PostDetailAuthorVerified>
            </PostDetailAuthorAvatar>
            <div>
              <PostDetailAuthorName>
                {publisher.name || "Analyst"}
              </PostDetailAuthorName>
              {publisher.title && (
                <PostDetailAuthorTitle>{publisher.title}</PostDetailAuthorTitle>
              )}
            </div>
          </PostDetailAuthorBlock>

          <PostDetailMetaDivider aria-hidden="true" />

          <PostDetailDateBlock>
            <PostDetailDateLabel>Publication Date</PostDetailDateLabel>
            <PostDetailDateValue dateTime={post.publishedAt}>
              {formatPostDetailDate(post.publishedAt)}
            </PostDetailDateValue>
          </PostDetailDateBlock>
        </PostDetailMetaRow>
      </PostDetailHeader>

      {showVideo && (
        <PostDetailVideoPlayer
          post={post}
          isPublicAccessHidden={isPublicAccessHidden}
        />
      )}

      <PostDetailGrid $hasSidebar={showSidebar}>
        <PostDetailContentCol>
          {!user && locked && (
            <>
              {excerptHtml && (
                <PostDetailExcerpt dangerouslySetInnerHTML={{ __html: excerptHtml }} />
              )}
              <PostDetailGateCard id="membership-options">
                <PostDetailGateIcon aria-hidden="true">
                  <PostDetailLockIcon />
                </PostDetailGateIcon>
                <PostDetailGateTitle>Members only</PostDetailGateTitle>
                <PostDetailGateText>
                  This analysis is part of Bitcoin.live membership. Sign in or start a
                  subscription to watch and read the full post.
                </PostDetailGateText>
                <PostDetailGateActions>
                  <PostDetailHeroButton as={Link} to="/#pricing">
                    View plans
                  </PostDetailHeroButton>
                  <PostDetailOutlineButton to="/login">Sign in</PostDetailOutlineButton>
                </PostDetailGateActions>
              </PostDetailGateCard>
            </>
          )}

          {user && locked && (
            <>
              {excerptHtml && (
                <PostDetailExcerpt dangerouslySetInnerHTML={{ __html: excerptHtml }} />
              )}
              <PostDetailGateCard>
                <PostDetailGateIcon aria-hidden="true">
                  <PostDetailLockIcon />
                </PostDetailGateIcon>
                <PostDetailGateTitle>Your access has expired</PostDetailGateTitle>
                <PostDetailGateText>
                  Your subscription has ended, so this post is locked. Renew to get back
                  to daily analyst coverage.
                </PostDetailGateText>
                <PostDetailGateActions>
                  <PostDetailHeroButton as={Link} to="/user-profile">
                    Renew membership
                  </PostDetailHeroButton>
                </PostDetailGateActions>
              </PostDetailGateCard>
            </>
          )}

          {!user && !locked && freePublic && publicAccessVerified !== true && (
            <>
              {excerptHtml && (
                <PostDetailExcerpt
                  dangerouslySetInnerHTML={{ __html: excerptHtml }}
                />
              )}
              {content && (
                <PostDetailEmailPreview>
                  <PostDetailHtmlContent dangerouslySetInnerHTML={{ __html: content }} />
                </PostDetailEmailPreview>
              )}
              <PostDetailEmailForm
                onSubmit={(event) => {
                  event.preventDefault();
                  subscribeToPublic();
                }}
              >
                <PostDetailGateIcon aria-hidden="true">
                  <PostDetailMailIcon />
                </PostDetailGateIcon>
                {publicAccessVerified === null && (
                  <>
                    <PostDetailGateTitle>Keep reading — it&apos;s free</PostDetailGateTitle>
                    <PostDetailEmailGateText>
                      Enter your name and email to unlock this public post. No subscription
                      required.
                    </PostDetailEmailGateText>
                    <PostDetailEmailFields>
                      <input
                        type="text"
                        placeholder="Name"
                        value={publicAccessName || ""}
                        onChange={onPublicNameInput}
                        aria-label="Name"
                      />
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={publicAccessEmail || ""}
                        onChange={onPublicEmailInput}
                        aria-label="Email address"
                        required
                      />
                      <PostDetailHeroButton type="submit">Unlock</PostDetailHeroButton>
                    </PostDetailEmailFields>
                  </>
                )}
                {publicAccessVerified === false && (
                  <>
                    <PostDetailGateTitle>Check your inbox</PostDetailGateTitle>
                    <PostDetailEmailGateText>
                      We sent a confirmation email. Once you&apos;ve verified your address,
                      refresh this page to unlock the article.
                    </PostDetailEmailGateText>
                    <PostDetailGateActions style={{ justifyContent: "flex-start" }}>
                      <PostDetailHeroButton type="button" onClick={checkPublicAccessConfirmed}>
                        Refresh
                      </PostDetailHeroButton>
                    </PostDetailGateActions>
                  </>
                )}
                {publicAccessError && (
                  <PostDetailEmailGateText style={{ color: "#f87171", marginTop: 16, marginBottom: 0 }}>
                    {publicAccessError}
                  </PostDetailEmailGateText>
                )}
              </PostDetailEmailForm>
            </>
          )}

          {showBody && content && (
            <>
              {hasFullAccess && excerptHtml && (
                <PostDetailExcerpt dangerouslySetInnerHTML={{ __html: excerptHtml }} />
              )}
              <PostDetailHtmlContent
                className={isPublicAccessHidden}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </>
          )}

          {hasFullAccess && (
            <PostDetailTagsFooter>
              <PostDetailTags>
                <PostDetailTag>{getPostTypeLabel(post)}</PostDetailTag>
                {post.tags?.slice(0, 4).map((tag) => (
                  <PostDetailTag key={tag.id || tag.name}>#{tag.name}</PostDetailTag>
                ))}
                {post.educationTopics?.map((topic) => (
                  <PostDetailTag key={topic.id || topic.name}>#{topic.name}</PostDetailTag>
                ))}
              </PostDetailTags>
              <PostDetailMetaStats>
                <span>
                  <PostDetailMessageCircleIcon width={14} height={14} />
                  {commentsCount}
                </span>
                <span>
                  <PostDetailCalendarIcon />
                  {formatPostDate(post.publishedAt)}
                </span>
              </PostDetailMetaStats>
            </PostDetailTagsFooter>
          )}

          {commentsSlot}
        </PostDetailContentCol>

        {showSidebar && (
          <PostDetailSidebar>
            <PostDetailGlassCard>
              <PostDetailSidebarTitle>Suggested posts</PostDetailSidebarTitle>
              <PostDetailSuggestedList>
                {suggestedPosts.map((item) => (
                  <PostDetailSuggestedLink
                    key={item.id}
                    to={item.url}
                    onClick={isPreview ? (event) => event.preventDefault() : undefined}
                    tabIndex={isPreview ? -1 : undefined}
                  >
                    <time dateTime={item.publishedAt}>
                      {formatPostDate(item.publishedAt)}
                    </time>
                    <h5>{item.title}</h5>
                    <PostDetailSuggestedMeta>
                      <PostDetailSuggestedAuthor>
                        {item.author?.name}
                      </PostDetailSuggestedAuthor>
                      <PostDetailSuggestedType>
                        {getPostTypeLabel(item)}
                      </PostDetailSuggestedType>
                    </PostDetailSuggestedMeta>
                  </PostDetailSuggestedLink>
                ))}
              </PostDetailSuggestedList>
            </PostDetailGlassCard>

            {locked && (
              <PostDetailSidebarCta
                as={Link}
                to={user ? "/user-profile" : "/#pricing"}
                onClick={isPreview ? (event) => event.preventDefault() : undefined}
                tabIndex={isPreview ? -1 : undefined}
              >
                {user ? "Renew Membership" : "Unlock Full Access"}
              </PostDetailSidebarCta>
            )}
          </PostDetailSidebar>
        )}
      </PostDetailGrid>
    </>
  );
};

export const PostDetailHeaderActions = ({
  post,
  user,
  likePost,
  setAsFavourite,
  removeAsFavorite,
  showShare,
  onShare,
  shareRef,
}) => {
  if (!post) return null;

  const publisher = post.author || {};
  const shareUrl =
    window.location.origin + post.url + "?aid=" + (publisher.affiliateId || "");
  const canShareSocial = post.contentAvailabilityId !== 2;
  const twitterUrl = canShareSocial
    ? "http://twitter.com/share?url=" +
        encodeURIComponent(
        window.location.origin +
          post.url +
          "?aid=" +
          publisher.slug +
          "&ms=2"
      ) +
      "&text=New premium (member) post by " +
      publisher.name +
      " was published on bitcoin.live."
    : null;
  const facebookUrl = canShareSocial
    ? "https://www.facebook.com/sharer/sharer.php?u=" +
      encodeURIComponent(
        window.location.origin +
          post.url +
          "?aid=" +
          publisher.slug +
          "&ms=3"
      )
    : null;

  return (
    <PostDetailActions>
      {user && (
        <PostDetailTooltip label="Like post">
          <PostDetailActionButton
            type="button"
            onClick={likePost}
            $active={post.likes?.likedByCurrentUser}
            aria-pressed={post.likes?.likedByCurrentUser}
            aria-label="Like post"
          >
            <span className="material-icons" aria-hidden="true">
              {post.likes?.likedByCurrentUser ? "favorite" : "favorite_border"}
            </span>
            {post.likes?.total ?? 0}
          </PostDetailActionButton>
        </PostDetailTooltip>
      )}

      {user && (
        <PostDetailTooltip
          label={post.isFavourite ? "Remove bookmark" : "Bookmark post"}
        >
          <PostDetailIconButton
            type="button"
            onClick={post.isFavourite ? removeAsFavorite : setAsFavourite}
            $active={post.isFavourite}
            aria-pressed={post.isFavourite}
            aria-label={post.isFavourite ? "Remove bookmark" : "Bookmark post"}
          >
            <span className="material-icons" aria-hidden="true">
              {post.isFavourite ? "bookmark" : "bookmark_border"}
            </span>
          </PostDetailIconButton>
        </PostDetailTooltip>
      )}

      {post.url && (
        <PostDetailTooltip label="Share post" hidden={showShare}>
          <PostDetailShareMenu
            url={shareUrl}
            twitterUrl={twitterUrl}
            facebookUrl={facebookUrl}
            show={showShare}
            onToggle={onShare}
            shareRef={shareRef}
          />
        </PostDetailTooltip>
      )}
    </PostDetailActions>
  );
};

export default PostDetailContent;
